import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, CalendarClock, BadgeCheck, CheckCircle2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

function StageTracker({ stage, steps }) {
  const idx = steps.findIndex((s) => s.key === stage);
  return (
    <div className="flex items-center gap-1 mt-3">
      {steps.map((s, i) => (
        <div key={s.key} className="flex-1">
          <div className={`h-1.5 rounded-full ${i <= idx ? "bg-[#0A251D]" : "bg-secondary"}`} />
          <div className={`text-[9px] uppercase tracking-wider mt-1 ${i <= idx ? "text-[#0A251D] font-bold" : "text-muted-foreground"}`}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function CandidateHome() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stages, setStages] = useState([]);

  const load = () => {
    api.get("/candidates").then((r) => setApps(r.data));
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/candidates/stages").then((r) => setStages(r.data.filter((s) => s.is_positive))).catch(() => {});
  }, []);
  useEffect(() => {
    if (apps.length) {
      Promise.all(apps.map((a) => api.get(`/interviews?candidate_id=${a.id}`).then((r) => r.data).catch(() => [])))
        .then((all) => setIvs(all.flat()));
    }
  }, [apps]);

  const respond = async (candId, response) => {
    try {
      await api.post(`/candidates/${candId}/interest`, { response });
      toast.success(response === "interested" ? "Great! We've notified the recruitment team." : "Thanks for letting us know.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const offerAction = async (offerId, status) => {
    try {
      await api.post(`/offers/${offerId}/status`, { status });
      toast.success(`Offer ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="candidate-portal">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Candidate Portal</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">Welcome, {user?.name?.split(" ")[0]}</h1>
      <p className="text-sm text-muted-foreground mt-1">Track your applications, confirm next steps and chat with our AI assistant.</p>

      {/* AI outreach — interest confirmation */}
      {apps.filter((a) => a.interest_status === "pending" && a.outreach).map((a) => (
        <div key={a.id} data-testid={`interest-card-${a.id}`} className="mt-6 border-2 border-[#E05A47] bg-white rounded-xl p-6">
          <div className="flex items-center gap-2 text-[#E05A47] text-[10px] uppercase tracking-[0.18em] font-bold"><Sparkles size={13} /> Message from TalentOS AI · {a.requisition_title}</div>
          <p className="text-sm leading-relaxed mt-3">{a.outreach.message}</p>
          <div className="flex gap-3 mt-4">
            <button data-testid={`interest-yes-${a.id}`} onClick={() => respond(a.id, "interested")}
              className="flex items-center gap-2 bg-[#0A251D] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
              <CheckCircle2 size={14} /> Yes, I&apos;m interested
            </button>
            <button data-testid={`interest-no-${a.id}`} onClick={() => respond(a.id, "not_interested")}
              className="flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-secondary transition-colors duration-150">
              <XCircle size={14} /> Not right now
            </button>
          </div>
        </div>
      ))}

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">My Applications ({apps.length})</div>
          {apps.map((a) => (
            <div key={a.id} data-testid={`my-application-${a.id}`} className="border bg-white rounded-xl p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-bold text-lg tracking-tight">{a.requisition_title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Applied via {a.source} · {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-bold ${a.stage === "rejected" ? "bg-red-100 text-red-700" : "bg-[#DDEAE3] text-[#133E31]"}`}>{a.stage}</span>
              </div>
              {a.stage !== "rejected" ? <StageTracker stage={a.stage} steps={stages} /> :
                <div className="text-xs text-muted-foreground mt-3">This application was not taken forward. Our AI assistant can suggest other open roles.</div>}
              {a.interest_status === "interested" && a.stage === "shortlisted" && (
                <div className="text-xs text-[#133E31] bg-[#DDEAE3] rounded-lg px-3 py-2 mt-3 font-semibold">You&apos;ve been shortlisted! The team will schedule your first interview shortly.</div>
              )}
            </div>
          ))}
          {apps.length === 0 && <div className="border bg-white rounded-xl p-6 text-sm text-muted-foreground">No applications yet — explore <a href="/careers" className="text-[#E05A47] font-bold hover:underline">open roles</a>.</div>}
        </div>

        <div className="space-y-4">
          <div className="border bg-white rounded-xl p-5" data-testid="my-interviews-panel">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3"><CalendarClock size={13} /> My Interviews</div>
            {ivs.length === 0 && <div className="text-sm text-muted-foreground">Nothing scheduled yet.</div>}
            {ivs.map((iv) => (
              <div key={iv.id} className="border-b last:border-0 py-2">
                <div className="text-sm font-semibold">{iv.round_name}{iv.round_order ? ` (Round ${iv.round_order}/${iv.total_rounds})` : ""}</div>
                <div className="text-xs text-muted-foreground capitalize">{iv.status.replace("_", " ")}{iv.scheduled_at ? ` · ${new Date(iv.scheduled_at).toLocaleString()}` : ""}</div>
              </div>
            ))}
          </div>
          <div className="border bg-white rounded-xl p-5" data-testid="my-offers-panel">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3"><BadgeCheck size={13} /> My Offers</div>
            {offers.length === 0 && <div className="text-sm text-muted-foreground">No offers yet.</div>}
            {offers.map((o) => (
              <div key={o.id} className="border-b last:border-0 py-3">
                <div className="text-sm font-semibold">₹{o.ctc_lpa} LPA — {o.designation || "Offer"}</div>
                <div className="text-xs text-muted-foreground capitalize">{o.status} · Joining {o.joining_date}</div>
                {o.status === "sent" && (
                  <div className="flex gap-2 mt-2">
                    <button data-testid={`portal-offer-accept-${o.id}`} onClick={() => offerAction(o.id, "accepted")} className="text-xs bg-[#0A251D] text-white px-3 py-1.5 font-bold hover:bg-[#133E31] transition-colors duration-150">Accept</button>
                    <button onClick={() => offerAction(o.id, "declined")} className="text-xs border px-3 py-1.5 font-bold hover:bg-secondary transition-colors duration-150">Decline</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Link to="/agent" data-testid="portal-ai-link" className="block border-2 border-[#0A251D] bg-white rounded-xl p-5 hover:bg-[#0A251D] hover:text-white transition-colors duration-150 group">
            <div className="flex items-center gap-2 font-heading font-bold"><Sparkles size={16} className="text-[#E05A47]" /> Ask TalentOS AI</div>
            <div className="text-xs mt-1 text-muted-foreground group-hover:text-[#A8C3B4]">Benefits, process, ICS Group — anything.</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
